// src/cockpit4/components/SimpleMarkdownRenderer.tsx
// ============================================
// 轻量级 Markdown 渲染器（用户需求 #8）
//
// 不依赖 react-markdown / remark-gfm，自行解析常见 Markdown 语法：
//   1. 去除 YAML frontmatter（--- 包裹的元数据块）
//   2. 渲染标题（# ## ### ####）
//   3. 渲染表格（| ... |）
//   4. 渲染列表（- item / 1. item）
//   5. 渲染粗体（**text**）
//   6. 渲染行内代码（`code`）
//   7. 渲染引用块（> text）
//   8. 渲染分隔线（---）
//   9. 普通段落
//
// 目的：替代 <pre>{content}</pre> 原样显示，避免"乱七八糟的符号"
// ============================================

import { useMemo } from "react";

interface SimpleMarkdownRendererProps {
  content: string;
  className?: string;
}

/** 去除 YAML frontmatter（--- 包裹的元数据块） */
function stripFrontmatter(content: string): string {
  if (!content.startsWith("---")) return content;
  const endIdx = content.indexOf("\n---", 3);
  if (endIdx === -1) return content;
  // 跳过结束的 --- 和后续空行
  return content.slice(endIdx + 4).replace(/^\s*\n/, "");
}

/** 渲染行内格式：粗体、行内代码、链接 */
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  // 正则匹配 **粗体**、`代码`、[链接](url)
  const regex = /(\*\*(.+?)\*\*)|(`(.+?)`)|(\[([^\]]+)\]\(([^)]+)\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let idx = 0;

  while ((match = regex.exec(text)) !== null) {
    // 添加匹配前的普通文本
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    // 处理匹配项
    if (match[1]) {
      // **粗体**
      nodes.push(
        <strong key={`${keyPrefix}-b-${idx}`} className="font-semibold text-white">
          {match[2]}
        </strong>
      );
    } else if (match[3]) {
      // `代码`
      nodes.push(
        <code key={`${keyPrefix}-c-${idx}`} className="px-1 py-0.5 rounded bg-cyan-500/10 text-cyan-300 text-[10px] font-mono">
          {match[4]}
        </code>
      );
    } else if (match[5]) {
      // [链接](url)
      nodes.push(
        <a key={`${keyPrefix}-a-${idx}`} href={match[7]} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">
          {match[6]}
        </a>
      );
    }
    lastIndex = match.index + match[0].length;
    idx++;
  }
  // 添加剩余文本
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

/** 解析表格行 */
function parseTableRow(line: string): string[] {
  return line.split("|").map((c) => c.trim()).filter((_, i, arr) => {
    // 过滤首尾空单元格（由前后 | 产生）
    return !(i === 0 && arr[0] === "") && !(i === arr.length - 1 && arr[arr.length - 1] === "");
  });
}

/** 判断是否为表格分隔行（|---|---|） */
function isTableSeparator(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.startsWith("|") || !trimmed.endsWith("|")) return false;
  return /^\|[\s-:]+\|[\s-:|]*$/.test(trimmed);
}

export default function SimpleMarkdownRenderer({ content, className = "" }: SimpleMarkdownRendererProps) {
  const rendered = useMemo(() => {
    const cleanContent = stripFrontmatter(content);
    const lines = cleanContent.split("\n");
    const elements: React.ReactNode[] = [];
    let i = 0;
    let key = 0;

    while (i < lines.length) {
      const line = lines[i];
      const trimmed = line.trim();

      // 空行
      if (!trimmed) {
        i++;
        continue;
      }

      // 标题（# ## ### ####）
      const headerMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
      if (headerMatch) {
        const level = headerMatch[1].length;
        const text = headerMatch[2];
        if (level === 1) {
          elements.push(
            <h1 key={key++} className="text-[16px] font-bold text-white mt-3 mb-2">
              {renderInline(text, `h1-${key}`)}
            </h1>
          );
        } else if (level === 2) {
          elements.push(
            <h2 key={key++} className="text-[14px] font-bold text-cyan-300 mt-3 mb-1.5 border-b border-cyan-500/20 pb-1">
              {renderInline(text, `h2-${key}`)}
            </h2>
          );
        } else if (level === 3) {
          elements.push(
            <h3 key={key++} className="text-[12px] font-semibold text-purple-300 mt-2 mb-1">
              {renderInline(text, `h3-${key}`)}
            </h3>
          );
        } else {
          elements.push(
            <h4 key={key++} className="text-[11px] font-medium text-gray-300 mt-2 mb-1">
              {renderInline(text, `h4-${key}`)}
            </h4>
          );
        }
        i++;
        continue;
      }

      // 分隔线（---）
      if (/^---+$/.test(trimmed) || /^\*\*\*+$/.test(trimmed)) {
        elements.push(
          <hr key={key++} className="my-3 border-white/[0.08]" />
        );
        i++;
        continue;
      }

      // 引用块（> text）
      if (trimmed.startsWith(">")) {
        const quoteLines: string[] = [];
        while (i < lines.length && lines[i].trim().startsWith(">")) {
          quoteLines.push(lines[i].trim().replace(/^>\s?/, ""));
          i++;
        }
        elements.push(
          <blockquote key={key++} className="my-2 pl-3 border-l-2 border-amber-500/40 bg-amber-500/[0.03] py-1.5 pr-2 rounded-r">
            <p className="text-[11px] text-amber-200/90 leading-relaxed">
              {renderInline(quoteLines.join(" "), `quote-${key}`)}
            </p>
          </blockquote>
        );
        continue;
      }

      // 表格（| ... |）
      if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
        const tableLines: string[] = [];
        while (i < lines.length && lines[i].trim().startsWith("|") && lines[i].trim().endsWith("|")) {
          tableLines.push(lines[i].trim());
          i++;
        }
        if (tableLines.length >= 2) {
          const headers = parseTableRow(tableLines[0]);
          // 跳过分隔行
          const dataStartIdx = isTableSeparator(tableLines[1]) ? 2 : 1;
          const rows = tableLines.slice(dataStartIdx).map(parseTableRow);

          elements.push(
            <div key={key++} className="my-2 overflow-x-auto">
              <table className="w-full text-[10px] font-mono border-collapse">
                <thead>
                  <tr>
                    {headers.map((h, idx) => (
                      <th key={idx} className="px-2 py-1 text-left text-cyan-300 border-b border-cyan-500/20 font-semibold whitespace-nowrap">
                        {renderInline(h, `th-${key}-${idx}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIdx) => (
                    <tr key={rowIdx} className="hover:bg-white/[0.02] transition-colors">
                      {row.map((cell, cellIdx) => (
                        <td key={cellIdx} className="px-2 py-1 text-gray-300 border-b border-white/[0.04]">
                          {renderInline(cell, `td-${key}-${rowIdx}-${cellIdx}`)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        continue;
      }

      // 无序列表（- item / * item）
      if (/^[-*]\s+/.test(trimmed)) {
        const listItems: string[] = [];
        while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
          listItems.push(lines[i].trim().replace(/^[-*]\s+/, ""));
          i++;
        }
        elements.push(
          <ul key={key++} className="my-1.5 space-y-0.5">
            {listItems.map((item, idx) => (
              <li key={idx} className="flex items-start gap-1.5 text-[11px] text-gray-300 leading-relaxed">
                <span className="text-cyan-400 mt-0.5 flex-shrink-0">•</span>
                <span>{renderInline(item, `li-${key}-${idx}`)}</span>
              </li>
            ))}
          </ul>
        );
        continue;
      }

      // 有序列表（1. item）
      if (/^\d+\.\s+/.test(trimmed)) {
        const listItems: string[] = [];
        while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
          listItems.push(lines[i].trim().replace(/^\d+\.\s+/, ""));
          i++;
        }
        elements.push(
          <ol key={key++} className="my-1.5 space-y-0.5">
            {listItems.map((item, idx) => (
              <li key={idx} className="flex items-start gap-1.5 text-[11px] text-gray-300 leading-relaxed">
                <span className="text-amber-400 mt-0.5 flex-shrink-0 font-mono text-[10px]">{idx + 1}.</span>
                <span>{renderInline(item, `ol-${key}-${idx}`)}</span>
              </li>
            ))}
          </ol>
        );
        continue;
      }

      // 普通段落
      elements.push(
        <p key={key++} className="text-[11px] text-gray-300 leading-relaxed my-1">
          {renderInline(trimmed, `p-${key}`)}
        </p>
      );
      i++;
    }

    return elements;
  }, [content]);

  return (
    <div className={`markdown-rendered ${className}`}>
      {rendered}
    </div>
  );
}
